import React, { useEffect } from 'react'
import FillingSpinner, { FillingSpinnerType } from 'components/FillingSpinner'
import Pagination from 'components/Pagination'
import { useTranslation } from 'react-i18next'
import Table, {
  TableBody,
  TableData,
  TableHeader,
  TableHeaderItem,
  TableRow,
} from 'components/Table'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { faTrashCan } from '@fortawesome/free-solid-svg-icons/faTrashCan'
import Button, { ButtonTheme } from 'components/Form/Button'
import PageHeader from 'components/Layout/PageHeader'
import { PRICE_LISTS_TABLE_ITEMS_PER_PAGE, DATE_FORMAT } from 'constant'
import { useNavigate } from 'react-router-dom'
import { Routes } from 'routes'
import ModalAction from 'components/Modal/ModalAction'
import { useDispatch, useSelector } from 'react-redux'
import DateUtils from 'utils/date'
import { RootState } from '../../../store'
import service from '../service'
import { priceListsTableActions } from './store/price-lists-table-slice'
import { PriceListTableItem } from '../models/price-lists-table-slice.model'
import { getPeriodDateRange } from '../models/price-list-form-slice.model'

const PriceListsTable: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { priceLists, totalCount, loading, loaded, pageNumber } = useSelector(
    (state: RootState) => state.priceListsTable
  )
  const { loading: modalLoading, priceList: removePriceListItem } = useSelector(
    (state: RootState) => state.priceListsTable.removeModal
  )

  const getPriceLists = (): void => {
    service.getPriceLists(pageNumber, PRICE_LISTS_TABLE_ITEMS_PER_PAGE)
  }

  useEffect(() => {
    getPriceLists()
  }, [pageNumber])

  useEffect(() => {
    if (!loaded) {
      getPriceLists()
    }
  }, [loaded, pageNumber])

  const Buttons = (priceList: PriceListTableItem) => (
    <>
      <Button
        icon={faEdit}
        tooltip={t('common.edit')}
        onClick={(): void => void navigate(Routes.PRICE_LISTS.EDIT(priceList.id))}
      />
      <Button
        theme={ButtonTheme.RED}
        icon={faTrashCan}
        tooltip={t('common.remove')}
        onClick={() => void dispatch(priceListsTableActions.setModalPriceList(priceList))}
      />
    </>
  )

  const PriceListsContent = (
    <Table className='CommonTable'>
      <TableHeader>
        <TableHeaderItem className='CommonTable__header-item'>{t('common.name')}</TableHeaderItem>
        <TableHeaderItem className='CommonTable__header-item'>
          {t('priceLists.fromDate')}
        </TableHeaderItem>
        <TableHeaderItem className='CommonTable__header-item'>
          {t('priceLists.endDate')}
        </TableHeaderItem>
        <TableHeaderItem className='CommonTable__header-item'>
          {t('common.actions')}
        </TableHeaderItem>
      </TableHeader>

      <TableBody>
        {priceLists.map((priceList: PriceListTableItem) => {
          const { startDate, endDate } = getPeriodDateRange(priceList.pricing)

          return (
            <TableRow
              key={priceList.id}
              className='CommonTable__data-row'
            >
              <TableData className='CommonTable__data-item CommonTable__data-item--name'>
                {priceList.name}
              </TableData>
              <TableData className='CommonTable__data-item'>
                {startDate ? DateUtils.parseAndFormat(startDate, DATE_FORMAT) : ''}
              </TableData>
              <TableData className='CommonTable__data-item'>
                {endDate ? DateUtils.parseAndFormat(endDate, DATE_FORMAT) : ''}
              </TableData>
              <TableData className='CommonTable__data-item CommonTable__data-item--buttons'>
                {Buttons(priceList)}
              </TableData>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )

  return (
    <div className='CommonTable__container'>
      <PageHeader
        link={Routes.PRICE_LISTS.NEW}
        linkTooltip={t('priceLists.create')}
      >
        {t('priceLists.title')}
      </PageHeader>

      {loading ? (
        <FillingSpinner type={FillingSpinnerType.STANDARD} />
      ) : (
        <>
          {!!priceLists.length && (
            <>
              {PriceListsContent}
              <Pagination
                activePage={pageNumber}
                itemsCountPerPage={PRICE_LISTS_TABLE_ITEMS_PER_PAGE}
                totalItemsCount={totalCount}
                onChange={(page: number) =>
                  void dispatch(priceListsTableActions.setPageNumber(page))
                }
              />
            </>
          )}
          {!priceLists.length && <p className='no-data'>{t('common.noDataFound')}</p>}
          {removePriceListItem && (
            <ModalAction
              title={t('modal.removePriceList')}
              itemText={t('modal.doYouWantToRemovePriceList')}
              itemName={removePriceListItem.name}
              actionText={t('common.remove')}
              isOpen={!!removePriceListItem}
              setIsOpen={() => void dispatch(priceListsTableActions.setModalPriceList(null))}
              isLoading={modalLoading}
              setIsLoading={isLoading =>
                void dispatch(priceListsTableActions.setModalLoading(isLoading))
              }
              onAction={() => service.removePriceList(removePriceListItem)}
            />
          )}
        </>
      )}
    </div>
  )
}

export default PriceListsTable
